// Frontend-Zugriff auf den ZWEI-Ebenen-Branchenkatalog (Backend-Route
// `contact-branchengruppe`). Reine Helfer (unit-testbar) plus ein modulweit
// gecachter Lade-Hook.
//
// Der dreistufige Altkatalog lebt unverändert in `brancheClassification.ts`
// weiter: Die Risikoanalyse führt ihre eingefrorenen Antwortsätze über dessen
// Ids, und der Bestand einer Firma bleibt darüber lesbar. Die beiden Kataloge
// zählen ihre Ids UNABHÄNGIG — Werte aus dem einen dürfen nie in ein Feld des
// anderen.

import * as React from "react";
import { apiUrl } from "../utils/api";

/** Der Name, unter dem beide Dropdowns ihre Freitext-Auffangzeile führen. */
export const BRANCHE_SONSTIGES = "Sonstiges";

/**
 * Die ECHTE Auffangbranche des Katalogs (Ebene 1) — eine Branche aus der Excel
 * mit dreizehn eigenen Gruppen (Sport, Kultur, Brauchtum, Land- &
 * Forstwirtschaft …) plus einer Auffanggruppe „Sonstiges".
 *
 * Sie wird angeboten und ans Listenende gepinnt.
 */
export const BRANCHE_AUFFANG_EBENE1 =
  "Sonstige (Landwirtschaft, Sport, Kultur, Brauchtum, Gesellschaft etc.)";

/**
 * Die KÜNSTLICHE Auffangbranche (Ebene 1), die der Katalog-Generator zusätzlich
 * erzeugt: genau eine Gruppe, ebenfalls „Sonstiges".
 *
 * Sie wird seit dem 09.09.2026 NICHT MEHR angeboten (PO): Zwei fast gleich
 * heißende Einträge im selben Dropdown sind für die Anwender nicht
 * unterscheidbar. Die Katalogzeile bleibt in der Datenbank stehen und ein
 * Kontakt, der sie gespeichert hat, zeigt und speichert sie weiterhin —
 * gefiltert wird ausschließlich das ANGEBOT (`angeboteneEbene1`).
 */
export const BRANCHE_AUFFANG_EBENE1_ALT = BRANCHE_SONSTIGES;

/** Eine Branchengruppe des Katalogs (entspricht der Backend-Entity). */
export interface BrancheGruppe {
  id: number;
  /** Ebene 1 — Branche gemäß KMVS. */
  ebene1: string;
  /** Ebene 2 — Branchengruppe. */
  ebene2: string;
  /** Blendet in der Maske ein Freitextfeld ein (nur „Sonstiges"). */
  erlaubtFreitext?: boolean;
  sortIndex?: number;
  active?: boolean;
}

/** Nur aktive Gruppen, in Katalog-Reihenfolge. */
export function activeGruppen(rows: BrancheGruppe[]): BrancheGruppe[] {
  return rows.filter((r) => r.active !== false);
}

/**
 * Gruppen, die eine Maske anbieten darf: alle aktiven PLUS die, die diese Firma
 * bereits gespeichert hat.
 *
 * WARUM die zweite Hälfte: Ein deaktivierter Eintrag verschwindet aus der
 * Auswahl — an einer Firma, die ihn trägt, aber nicht. Böte die Maske ihn nicht
 * an, schickte der nächste Speichervorgang die Auswahl ohne ihn: Die Firma
 * verlöre ihre Branche, ohne dass jemand sie angefasst hätte. Wortgleich zum
 * `waehlbareLeaves`-Pfad des Altkatalogs, und aus demselben Grund.
 */
export function waehlbareGruppen(
  rows: BrancheGruppe[],
  gewaehlt: Iterable<number> = [],
): BrancheGruppe[] {
  const ids = new Set(gewaehlt);
  return rows.filter((r) => r.active !== false || ids.has(r.id));
}

/** Ist die Gruppe deaktiviert (und damit nur noch als Bestandswert sichtbar)? */
export function istDeaktiviert(gruppe: BrancheGruppe | undefined): boolean {
  return gruppe?.active === false;
}

/**
 * Distinct Branchen (Ebene 1) in Katalog-Reihenfolge. `gewaehlt` hält die
 * Branche einer bereits gespeicherten (deaktivierten) Gruppe in der Liste —
 * sonst stünde das Branchenfeld der Firma leer da.
 */
export function distinctEbene1(
  rows: BrancheGruppe[],
  gewaehlt: Iterable<number> = [],
): string[] {
  const out: string[] = [];
  for (const r of waehlbareGruppen(rows, gewaehlt)) {
    if (!out.includes(r.ebene1)) out.push(r.ebene1);
  }
  return out;
}

/** Die Gruppen einer Branche, in Katalog-Reihenfolge („Sonstiges" zuletzt). */
export function gruppenFuer(
  rows: BrancheGruppe[],
  ebene1: string,
  gewaehlt: Iterable<number> = [],
): BrancheGruppe[] {
  return waehlbareGruppen(rows, gewaehlt).filter((r) => r.ebene1 === ebene1);
}

// --- Anzeige-Reihenfolge und Angebot ----------------------------------------
//
// WARUM getrennt von `distinctEbene1`/`gruppenFuer`: Jene beiden speisen nicht
// nur die Optionslisten, sondern auch Auswahlpfade (`gruppeAutomatisch`).
// Würde dort sortiert, schlüge die Anzeige-Reihenfolge in die AUSWAHL durch —
// und die erste Zeile der Auswahl ist die Hauptbranche, die zusätzlich am
// Kontakt steht. Nichts schlüge fehl; die Firma stünde danach nur unter der
// falschen Branche. Sortiert wird deshalb ausschließlich hier.

/** Deutsche Sortierung: Umlaute einsortieren, nicht ans Ende hängen. */
const deutsch = (a: string, b: string): number => a.localeCompare(b, "de");

/**
 * Ist das eine Auffangbranche (Ebene 1)? Beide Schreibweisen zählen — die echte
 * (a) und die künstliche (b), die nur noch als Bestandswert vorkommt. Beide
 * gehören ans Listenende, egal wie sie alphabetisch fallen.
 */
export function istAuffangBranche(ebene1: string | null | undefined): boolean {
  const name = (ebene1 ?? "").trim();
  return name === BRANCHE_AUFFANG_EBENE1 || name === BRANCHE_AUFFANG_EBENE1_ALT;
}

/**
 * Sortiert Ebene-1-Namen alphabetisch (deutsch) und pinnt die Auffangbranchen
 * ans Ende. Muster wie in `finanzaemter.ts`: erst Rang, dann Alphabet.
 */
export function sortiereEbene1(namen: string[]): string[] {
  const rang = (n: string) => (istAuffangBranche(n) ? 1 : 0);
  return [...namen].sort((a, b) => rang(a) - rang(b) || deutsch(a, b));
}

/**
 * Die Branchen für ein Dropdown: alphabetisch, Auffangbranchen zuletzt.
 * `gewaehlt` hält — wie bei `distinctEbene1` — die Branche einer bereits
 * gespeicherten, deaktivierten Gruppe in der Liste.
 */
export function sortierteEbene1(
  rows: BrancheGruppe[],
  gewaehlt: Iterable<number> = [],
): string[] {
  return sortiereEbene1(distinctEbene1(rows, gewaehlt));
}

/**
 * Die Gruppen einer Branche für ein Dropdown: alphabetisch, die Auffanggruppe
 * zuletzt.
 *
 * ERKANNT WIRD SIE ÜBER `erlaubtFreitext`, NIEMALS ÜBER DEN NAMEN: Der Katalog
 * führt sieben ganz normale Gruppen, die „Sonstige …" bzw. „Sonstiger …"
 * heißen (etwa „Sonstiger Einzel- & Fachhandel"). Ein Namensvergleich zöge sie
 * ans Ende und blendete ihnen ein Freitextfeld ein, das das Backend verwirft.
 */
export function sortierteGruppen(
  rows: BrancheGruppe[],
  ebene1: string,
  gewaehlt: Iterable<number> = [],
): BrancheGruppe[] {
  const rang = (g: BrancheGruppe) => (g.erlaubtFreitext === true ? 1 : 0);
  return [...gruppenFuer(rows, ebene1, gewaehlt)].sort(
    (a, b) => rang(a) - rang(b) || deutsch(a.ebene2, b.ebene2),
  );
}

/**
 * Die Branchen, die das Dropdown ANBIETET: `sortierteEbene1` ohne die
 * künstliche Auffangbranche (b).
 *
 * Der Bestandsschutz ist derselbe wie in `waehlbareGruppen`: Zeigt eine der
 * gewählten ids auf (b), bleibt sie in der Liste. Ohne das zeigte ein
 * Bestandskontakt ein leer wirkendes Branchenfeld, und der nächste
 * Speichervorgang schickte die Auswahl ohne sie — die Firma verlöre ihre
 * Branche, ohne dass jemand sie angefasst hätte.
 *
 * WICHTIG: Gefiltert wird NUR hier, beim Bauen der Optionsliste — nie in den
 * Katalogdaten und nie in `waehlbareGruppen`. Sonst fände `findGruppe` die
 * gespeicherte id nicht mehr und `bloeckeAusAuswahl` würfe den Bestandswert in
 * einen Block ohne Branche.
 */
export function angeboteneEbene1(
  rows: BrancheGruppe[],
  gewaehlt: Iterable<number> = [],
): string[] {
  const ids = new Set(gewaehlt);
  const imBestand = rows.some(
    (r) => ids.has(r.id) && r.ebene1.trim() === BRANCHE_AUFFANG_EBENE1_ALT,
  );
  return sortierteEbene1(rows, ids).filter(
    (e) => imBestand || e.trim() !== BRANCHE_AUFFANG_EBENE1_ALT,
  );
}

/** Gruppe per id finden (auch inaktive, damit Bestandswerte anzeigbar bleiben). */
export function findGruppe(
  rows: BrancheGruppe[],
  id: number | null | undefined,
): BrancheGruppe | undefined {
  if (id == null) return undefined;
  return rows.find((r) => r.id === id);
}

/** Blendet diese Gruppe ein Freitextfeld ein? */
export function erlaubtFreitext(
  rows: BrancheGruppe[],
  id: number | null | undefined,
): boolean {
  return findGruppe(rows, id)?.erlaubtFreitext === true;
}

/** Anzeigepfad „Branche / Branchengruppe". */
export function formatGruppePfad(gruppe: BrancheGruppe | undefined): string {
  if (!gruppe) return "";
  return [gruppe.ebene1, gruppe.ebene2].filter(Boolean).join(" / ");
}

/**
 * Anzeigepfad mit dem „Sonstiges"-Freitext in Klammern.
 *
 * Der Text hängt nur an Gruppen, die ihn erlauben: Stünde im Bestand doch einer
 * an einer normalen Gruppe, wäre er ein stiller Zweitwert, den die Maske gar
 * nicht mehr anbietet — ihn anzuzeigen behauptete eine Pflegbarkeit, die es
 * nicht gibt.
 */
export function formatGruppeMitFreitext(
  gruppe: BrancheGruppe | undefined,
  freitext: string | null | undefined,
): string {
  const pfad = formatGruppePfad(gruppe);
  if (!pfad || !gruppe?.erlaubtFreitext) return pfad;
  const text = (freitext ?? "").trim();
  return text ? `${pfad} (${text})` : pfad;
}

// --- modulweit gecachter Loader ---------------------------------------------

let cache: BrancheGruppe[] | null = null;
let inflight: Promise<BrancheGruppe[]> | null = null;

/** Wirft den modulweiten Cache weg (nach einer Katalogänderung). */
export function invalidateBrancheGruppen(): void {
  cache = null;
  inflight = null;
}

async function fetchGruppen(): Promise<BrancheGruppe[]> {
  if (cache) return cache;
  if (!inflight) {
    inflight = fetch(apiUrl("contact-branchengruppe"))
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        cache = Array.isArray(d) ? (d as BrancheGruppe[]) : [];
        return cache;
      })
      .catch(() => {
        cache = [];
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Katalog außerhalb von React laden (derselbe modulweite Cache wie der Hook). */
export function brancheGruppenLaden(): Promise<BrancheGruppe[]> {
  return fetchGruppen();
}

/** Lädt den Katalog einmalig (modulweit gecacht) und liefert Zeilen + Ladezustand. */
export function useBrancheGruppen(): { rows: BrancheGruppe[]; loading: boolean } {
  const [rows, setRows] = React.useState<BrancheGruppe[]>(cache ?? []);
  const [loading, setLoading] = React.useState<boolean>(cache == null);

  React.useEffect(() => {
    let alive = true;
    if (cache) {
      setRows(cache);
      setLoading(false);
      return;
    }
    fetchGruppen().then((r) => {
      if (!alive) return;
      setRows(r);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { rows, loading };
}
